import type { PreviewFormat } from '../models/viewer-types';

const guidPrefix = /^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?=[._-]|$)/i;

/** What the grouping reads of an entry; the rest of it travels through untouched. */
interface NamedEntry {
  name: string;
  directory: boolean;
}

type GroupedEntry<TEntry> = TEntry & { displayName?: string; previewFormat?: PreviewFormat };

/**
 * Display paths for the entries of an MSP360 backup archive: a plan and its logs read as folders under
 * the plan id. Physical entries and their byte ranges are left alone — only the names for the tree are
 * added, so the preview still reads the very same bytes.
 *
 * @param {Array<TEntry>} entries Entries as the archive lists them.
 * @return {Array<GroupedEntry<TEntry>>} The same entries, named for the tree.
 */
export function groupMsp360Archive<TEntry extends NamedEntry>(entries: Array<TEntry>): Array<GroupedEntry<TEntry>> {
  return entries.map((entry) => {
    if (entry.directory || entry.name.includes('/')) return entry;

    const match = guidPrefix.exec(entry.name);

    if (!match) return entry;

    const guid = match[1].toLowerCase();
    const backup = /\.cbb\.(.+)\.bak$/i.exec(entry.name);
    const log = /\.log(?:\.(.+))?$/i.exec(entry.name);

    if (/\.cbb$/i.test(entry.name) || backup) {
      return { ...entry, displayName: `${guid}/plan/${backup ? backup[1] : 'current'}.cbb`, previewFormat: 'xml' as const };
    }

    if (log) {
      return { ...entry, displayName: `${guid}/logs/${log[1] || '0'}.log`, previewFormat: 'msp360-log' as const };
    }

    return { ...entry, displayName: `${guid}/${entry.name}` };
  });
}
